﻿import React, { useContext, useState, useEffect } from 'react';
import {Helmet} from "react-helmet";
import { useTranslation } from 'react-i18next';
import Context, { context_type } from '@Util/Context';

import { H2 } from '@Elements/Fonts';
import Content_Container from '@Elements/Content_Container';
import Input_With_Label from '@Elements/FormElements/Input_With_Label';
import Checkbox from '@Elements/FormElements/Checkbox';
import Submit_Button from '@Elements/FormElements/Submit_Button';
import { str_isEmpty, validate_email } from '@Util/Utils';
import * as Api from '@Util/Api';
import { apiBase } from '@Util/GlobalValues';

export default function Settingspage(): JSX.Element {
    const [t] = useTranslation();
    const {
        logged_in_user,
        set_status_update
    }: context_type = useContext(Context);

    const [first_name, set_first_name] = useState<string>("");
    const [last_name, set_last_name] = useState<string>("");
    const [email, set_email] = useState<string>("");
    const [employment_role, set_employment_role] = useState<string>("");
    const [display_pomodoro, set_display_pomodoro] = useState<boolean>(false);
    const [show_timelog_table_as_default, set_show_timelog_table_as_default] = useState<boolean>(false);
    const [display_client_website_errors, set_display_client_website_errors] = useState<boolean>(false);
    const [display_new_intime_version, set_display_new_intime_version] = useState<boolean>(false);
    const [display_module_recent_projects, set_display_module_recent_projects] = useState<boolean>(false);
    const [display_module_all_projects, set_display_module_all_projects] = useState<boolean>(false);

    const [first_name_error, set_first_name_error] = useState<boolean>(false);
    const [last_name_error, set_last_name_error] = useState<boolean>(false);
    const [email_error, set_email_error] = useState<boolean>(false);
    const [employment_role_error, set_employment_role_error] = useState<boolean>(false);
    const [person_information_saving, set_person_information_saving] = useState<boolean>(false);
    const [preference_saving, set_preference_saving] = useState<boolean>(false);

    useEffect(() => {
        if (logged_in_user) {
            set_first_name(logged_in_user.firstName);
            set_last_name(logged_in_user.lastName);
            set_email(logged_in_user.email);
            set_employment_role(logged_in_user?.settings?.employeeRole);
            set_display_pomodoro(logged_in_user?.settings?.displayPomodoro);
            set_show_timelog_table_as_default(logged_in_user?.settings?.defaultShowTimelogTableOnStartpage);
            set_display_client_website_errors(logged_in_user?.settings?.displayClientWebsiteErrors);
            set_display_new_intime_version(logged_in_user?.settings?.displayNewInTimeVersion);
            set_display_module_recent_projects(logged_in_user?.settings?.displayModuleRecentProjects);
            set_display_module_all_projects(logged_in_user?.settings?.displayModuleAllProjects);
        }
    }, [logged_in_user]);

    function reset_input_errors(): void {
        set_first_name_error(false);
        set_last_name_error(false);
        set_email_error(false);
        set_employment_role_error(false);
    }

    function validate_person_information_inputs(): boolean {
        reset_input_errors();

        if (str_isEmpty(first_name)) {
            set_first_name_error(true);
            return false;
        }

        if (str_isEmpty(last_name)) {
            set_last_name_error(true);
            return false;
        }

        if (str_isEmpty(email) && validate_email(email)) {
            set_email_error(true);
            return false;
        }

        return true;
    }

    async function handle_personal_information_submit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        try {
            set_person_information_saving(true);

            if (validate_person_information_inputs()) {
                const api_call = await Api.callApi(`${apiBase}/UpdateUserInformation`, {
                    id: logged_in_user.id,
                    firstName: first_name,
                    lastName: last_name,
                    email: email,
                    employmentRole: employment_role
                });

                if (api_call?.status === "Invalid" || api_call?.status === "Error") {
                    set_status_update({
                        visible: true,
                        text: api_call?.details,
                        type: "Error"
                    })
                } else {
                    set_status_update({
                        visible: true,
                        text: t('Text.ApiSaved'),
                        type: "Success"
                    })
                }

                set_person_information_saving(false);
            } else {

                set_person_information_saving(false);
            }
        } catch (err) {
            console.error(err.message);
        }
    } 

    async function handle_preference_submit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        try {
            set_preference_saving(true);

            if (validate_person_information_inputs()) {
                const api_call = await Api.callApi(`${apiBase}/UpdatePreferences`, {
                    id: logged_in_user.id,
                    displayPomodoro: display_pomodoro,
                    defaultShowTimelogTableOnStartpage: show_timelog_table_as_default,
                    displayClientWebsiteErrors: display_client_website_errors,
                    displayNewInTimeVersion: display_new_intime_version,
                    displayModuleRecentProjects: display_module_recent_projects,
                    displayModuleAllProjects: display_module_all_projects
                });

                if (api_call?.status === "Invalid" || api_call?.status === "Error") {
                    set_status_update({
                        visible: true,
                        text: api_call?.details,
                        type: "Error"
                    });
                } else {
                    set_status_update({
                        visible: true,
                        text: t('Text.ApiSaved'),
                        type: "Success"
                    });

                    
                    // IF DISPLAY NEW INTIME VERSION IS TRUE, RELOAD THE PAGE.
                    if (!display_new_intime_version) {
                        setTimeout(function () {
                            window.location.href = "/d";
                        }, 1500);
                    }
                }

                set_preference_saving(false);
            } else {
                set_preference_saving(false);
            }
        } catch (err) {
            console.error(err.message);
        }
    }

    return (
        <>
            <Helmet>
                <title>{`${t('Navigation.Settings')} - InTime`}</title>
            </Helmet>
            <div className="Page_Content_Grid">
                <section className="Page_Content_Grid_Section">
                    <section className="Module">
                        <header className="Module_Header">
                            <H2>{t('Text.PersonalInformation')}</H2>
                        </header>
                        <Content_Container>
                            <form onSubmit={handle_personal_information_submit}>
                                <Input_With_Label
                                    id="settings_first_name"
                                    type="text"
                                    label={t('Label.FirstName')}
                                    value={first_name}
                                    onChange={(e) => set_first_name(e.target.value)}
                                    error={first_name_error}
                                />
                                <Input_With_Label
                                    id="settings_last_name"
                                    type="text"
                                    label={t('Label.LastName')}
                                    value={last_name}
                                    onChange={(e) => set_last_name(e.target.value)}
                                    error={last_name_error}
                                />
                                <Input_With_Label
                                    id="settings_email"
                                    type="email"
                                    label={t('Label.Email')}
                                    value={email}
                                    onChange={(e) => set_email(e.target.value)}
                                    error={email_error}
                                />
                                <Input_With_Label
                                    id="settings_employee_role"
                                    type="text"
                                    label={t('Label.EmployeeRole')}
                                    value={employment_role}
                                    onChange={(e) => set_employment_role(e.target.value)}
                                    error={employment_role_error}
                                />
                                <Submit_Button
                                    text={t('Text.Update')}
                                    disabled={person_information_saving}
                                    loading={person_information_saving}
                                />
                            </form>
                        </Content_Container>
                    </section>
                </section>
                <section className="Page_Content_Grid_Section">
                    <section className="Module">
                        <header className="Module_Header">
                            <H2>{t('Text.Preferences')}</H2>
                        </header>
                        <Content_Container>
                            <form onSubmit={handle_preference_submit}>
                                <Checkbox
                                    id="settings_display_pomodoro"
                                    label={t('Label.DisplayPomodoro')}
                                    checked={display_pomodoro}
                                    onChange={(e) => set_display_pomodoro(e.target.checked)}
                                    error={false}
                                />
                                <Checkbox
                                    id="settings_show_timelog_table_as_default"
                                    label={t('Label.DisplayTimelogTableAsDefault')}
                                    checked={show_timelog_table_as_default}
                                    onChange={(e) => set_show_timelog_table_as_default(e.target.checked)}
                                    error={false}
                                />
                                <Checkbox
                                    id="settings_display_client_website_errors"
                                    label={t('Label.DisplayClientWebsiteErrors')}
                                    checked={display_client_website_errors}
                                    onChange={(e) => set_display_client_website_errors(e.target.checked)}
                                    error={false}
                                />
                                <Checkbox
                                    id="settings_display_recent_projects_module"
                                    label={t('Label.ShowRecentProjectsModule')}
                                    checked={display_module_recent_projects}
                                    onChange={(e) => set_display_module_recent_projects(e.target.checked)}
                                    error={false}
                                />
                                <Checkbox
                                    id="settings_display_all_projects_module"
                                    label={t('Label.ShowAllProjectsModule')}
                                    checked={display_module_all_projects}
                                    onChange={(e) => set_display_module_all_projects(e.target.checked)}
                                    error={false}
                                />
                                <Checkbox
                                    id="settings_display_new_intime_version"
                                    label={t('Label.DisplayInTime2')}
                                    checked={display_new_intime_version}
                                    onChange={(e) => set_display_new_intime_version(e.target.checked)}
                                    error={false}
                                />
                                <Submit_Button
                                    text={t('Text.Update')}
                                    disabled={preference_saving}
                                    loading={preference_saving}
                                />
                            </form>
                        </Content_Container>
                    </section>
                </section>
            </div>
        </>
    )
}